<template>
	<TaskListRowSkeleton
		:is-subtask="isSubtask"
		:show-subtasks="props.showSubtasks"
		:animate="loading"
		:col-definition="props.colDefinition"
	>
		<div class="task-list-row-task-bundle">
			<VMenu
				:triggers="[]"
				:hide-triggers="[]"
				:popper-hide-triggers="[]"
				:auto-hide="false"
				:delay="0"
				shown
				placement="top-start"
			>
				<template #popper>
					<SuiModalDialog>
						<template #header>
							<h2>{{ title }}</h2>
						</template>

						<TaskBundleAutoSuggest
							id="task-list-row-task-bundle"
							class="task-list-row-task-bundle__auto-suggest"
							:item="item"
							name="task-list-row-task-bundle"
							@input="setSelected"
						/>

						<template #footer>
							<SuiButton
								variant="secondary-outlined"
								@click="closeModal"
							>
								{{ $t('cancel') }}
							</SuiButton>
							<SuiButton
								:is-disabled="loading"
								:is-loading="loading"
								variant="primary"
								@click="onSave"
							>
								{{ $t('save') }}
							</SuiButton>
						</template>
					</SuiModalDialog>
				</template>
			</VMenu>
		</div>
	</TaskListRowSkeleton>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import {
	type Project,
	type ProjectPhase,
	type ProjectPhasesAndTaskSetsSaveInput,
	type Task,
	type TaskListProjectFragment,
	useProjectPhasesAndTaskSetsSaveMutation,
	useTaskListRowTaskBundleSaveMutation,
} from '@/generated/graphql';
import {
	SuiModalDialog,
	SuiButton,
	type SuiDataTableColumnType,
} from '@developers/scoro-ui';
import TaskListRowSkeleton from './TaskListRowSkeleton.vue';
import { TaskBundleAutoSuggest } from '@/features/TaskBundle';
import { type TaskSetItem } from '@/features/ProjectPhase';
import { useTaskListStore } from '../store';
import { useTranslation } from '@/composables';
import { TaskBundlePlacement } from '../types';

export type TaskListRowTaskBundleProps = {
	projectId?: Project['id'];
	projectPhaseId?: ProjectPhase['id'];
	projectPhases?: TaskListProjectFragment['projectPhases'];
	parentTaskId?: Task['id'];
	showSubtasks?: boolean;
	colDefinition?: SuiDataTableColumnType[];
};

const props = defineProps<TaskListRowTaskBundleProps>();

const item = {} as TaskSetItem;
const selectedTaskBundle = ref<TaskSetItem>();

const taskListStore = useTaskListStore();
const { t } = useTranslation();
const {
	loading: loadingtaskSetasProjectTasksSave,
	mutate: taskSetasProjectTasksSave,
} = useProjectPhasesAndTaskSetsSaveMutation();
const { loading: loadingTaskSetAsSubtasksSave, mutate: taskSetAsSubtasksSave } =
	useTaskListRowTaskBundleSaveMutation();

const loading = computed(() => {
	return (
		loadingtaskSetasProjectTasksSave.value ||
		loadingTaskSetAsSubtasksSave.value
	);
});

const setSelected = (value: TaskSetItem) => {
	selectedTaskBundle.value = value;
};

const closeModal = () => {
	selectedTaskBundle.value = undefined;

	taskListStore.$patch({
		taskBundlePlacement: TaskBundlePlacement.Top,
		taskBundleOpen: undefined,
		subtaskBundleOpen: undefined,
	});
};

const saveTaskSetAsSubtasks = async () => {
	if (!selectedTaskBundle.value || !props.parentTaskId) {
		return;
	}

	const result = await taskSetAsSubtasksSave({
		input: {
			taskSetId: selectedTaskBundle.value.id,
			parentId: props.parentTaskId,
		},
	});

	if (result?.data?.TaskSetAsSubtasksSave) {
		closeModal();
	}
};

const saveTaskSetAsProjectTasks = async () => {
	if (!selectedTaskBundle.value || !props.projectId) {
		return;
	}

	const input: ProjectPhasesAndTaskSetsSaveInput = {
		projectId: props.projectId,
		isSaveOnlyTaskSets: true,
		phases: props.projectPhases?.map((phase) => ({
			id: phase.id,
			title: phase.title,
			type: phase.type,
		})),
	};

	if (props.projectPhaseId && input.phases) {
		const currentPhaseIndex = input.phases.findIndex(
			({ id }) => id === props.projectPhaseId
		);

		input.phases[currentPhaseIndex].taskSets = [
			{
				id: selectedTaskBundle.value.id,
			},
		];
	} else {
		input.taskSets = [
			{
				id: selectedTaskBundle.value.id,
			},
		];
	}

	const result = await taskSetasProjectTasksSave({
		input,
	});

	if (result?.data?.ProjectPhasesAndTaskSetsSave) {
		taskListStore.taskBundleOpen = undefined;
	}
};

const onSave = async () => {
	if (selectedTaskBundle.value && props.parentTaskId) {
		saveTaskSetAsSubtasks();
	}

	if (selectedTaskBundle.value && props.projectId) {
		saveTaskSetAsProjectTasks();
	}

	return;
};

const title = computed(() => {
	if (props.parentTaskId) {
		return t('add_task_set_as_subtasks');
	}

	return t('add_task_set');
});

const isSubtask = computed(() => !!taskListStore.subtaskBundleOpen);
</script>

<style lang="scss">
.task-list-row-task-bundle {
	&__auto-suggest {
		width: 40rem;
	}
}
</style>
